// src/App.js
import React, { useEffect, useState, useRef } from "react";
import { auth, db, googleProvider } from "./firebase";
import { signInWithPopup, signOut, onAuthStateChanged } from "firebase/auth";
import {
  doc,
  getDoc,
  setDoc,
  collection,
  onSnapshot,
  addDoc,
  deleteDoc,
  updateDoc,
  query,
  where,
} from "firebase/firestore";
import TrainerView from "./views/TrainerView";
import EmployeeView from "./views/EmployeeView";
import "./index.css";

export default function App() {
  const [user, setUser] = useState(null);
  const [role, setRole] = useState(null); // "trainer" | "employee" | null
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [avatarOpen, setAvatarOpen] = useState(false);
  const [loginError, setLoginError] = useState("");
  const avatarRef = useRef(null);

  useEffect(() => {
    const handleClick = (e) => {
      if (avatarRef.current && !avatarRef.current.contains(e.target)) {
        setAvatarOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  // Auth state listener
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        setUser(firebaseUser);
        await loadOrCreateUserRole(firebaseUser);
      } else {
        setUser(null);
        setRole(null);
      }
      setLoading(false);
    });
    return unsub;
  }, []);

  // Load role from Firestore, create employee doc if first login
  const loadOrCreateUserRole = async (firebaseUser) => {
    const userRef = doc(db, "users", firebaseUser.uid);
    const snap = await getDoc(userRef);

    if (snap.exists()) {
      setRole(snap.data().role);
    } else {
      // First login → default role is employee
      await setDoc(userRef, {
        email: firebaseUser.email,
        name: firebaseUser.displayName || firebaseUser.email,
        role: "employee",
        createdAt: new Date(),
      });
      setRole("employee");
    }
  };

  // Live sessions listener.
  // De rules staan een onbegrensde query op /sessions alleen toe voor
  // trainer/admin. Een medewerker vraagt daarom expliciet op waar hij recht op
  // heeft: sessies voor zijn domein en sessies waarin hij persoonlijk is
  // uitgenodigd. Zonder die filters weigert Firestore de query volledig.
  useEffect(() => {
    if (!user || !role) {
      setSessions([]);
      return;
    }
    const sessionsRef = collection(db, "sessions");
    const toDocs = (snap) => snap.docs.map((d) => ({ id: d.id, ...d.data() }));

    if (role === "trainer" || role === "admin") {
      return onSnapshot(sessionsRef, (snap) => setSessions(toDocs(snap)));
    }

    const email = (user.email || "").toLowerCase();
    const domain = email.split("@")[1] || "";

    // Twee streams samenvoegen; Firestore kent geen OR over verschillende velden
    const parts = { byDomain: [], byEmail: [] };
    const publish = () => {
      const merged = new Map();
      [...parts.byDomain, ...parts.byEmail].forEach((s) => merged.set(s.id, s));
      setSessions([...merged.values()]);
    };
    const onError = (err) => console.error("sessions listener:", err);

    const u1 = onSnapshot(
      // Geen "*" hier: de rules erkennen het wildcard-domein niet meer, en een
      // query die buiten de rules kan vallen wordt in zijn geheel geweigerd.
      query(sessionsRef, where("allowedDomains", "array-contains-any", [`@${domain}`, domain])),
      (snap) => { parts.byDomain = toDocs(snap); publish(); },
      onError,
    );
    const u2 = onSnapshot(
      query(sessionsRef, where("allowedEmails", "array-contains", email)),
      (snap) => { parts.byEmail = toDocs(snap); publish(); },
      onError,
    );
    return () => { u1(); u2(); };
  }, [user, role]);

  const handleLogin = async () => {
    setLoginError("");
    try {
      await signInWithPopup(auth, googleProvider);
    } catch (err) {
      console.error("Login failed:", err);
      // Een blocking function weigert accounts buiten de bekende domeinen.
      // Firebase geeft dat terug als internal-error met onze melding erin.
      const code = err?.code || "";
      const msg = String(err?.message || "");
      if (code === "auth/popup-closed-by-user" || code === "auth/cancelled-popup-request") {
        return;
      }
      if (msg.includes("geen toegang") || code === "auth/internal-error" || code === "auth/admin-restricted-operation") {
        setLoginError("Dit account heeft geen toegang tot het trainingsplatform. Log in met je werkaccount.");
      } else {
        setLoginError("Inloggen is niet gelukt. Probeer het opnieuw.");
      }
    }
  };

  const handleLogout = () => signOut(auth);

  // Trainer: add new session → cloud function picks this up and creates calendar event
  const handleAddSession = async (data) => {
    await addDoc(collection(db, "sessions"), {
      ...data,
      status: "new",
      masterCalendarEventId: null,
      meetLink: "",
      createdAt: new Date(),
    });
  };

  // Trainer: update existing session
  const handleUpdateSession = async (sessionId, data) => {
    await updateDoc(doc(db, "sessions", sessionId), data);
  };

  // Trainer: delete session → cloud function deletes calendar event
  const handleDeleteSession = async (session) => {
    await deleteDoc(doc(db, "sessions", session.id));
  };

  // Employee: register → write to subcollection → cloud function syncs calendar
  const handleRegister = async (session) => {
    if (!user) return;
    const attendeeRef = doc(db, "sessions", session.id, "attendees", user.uid);
    await setDoc(attendeeRef, {
      email: user.email,
      name: user.displayName || user.email,
      uid: user.uid,
      registeredAt: new Date(),
    });
  };

  // Employee: unregister → delete from subcollection → cloud function syncs calendar
  const handleUnregister = async (session) => {
    if (!user) return;
    const attendeeRef = doc(db, "sessions", session.id, "attendees", user.uid);
    await deleteDoc(attendeeRef);
  };

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="login-screen">
        <div className="login-card">
          <img
            className="login-logo"
            src="https://adapta.nl/wp-content/uploads/2023/11/adaptalogos_workspace.svg"
            alt="Adapta"
          />
          <h1>Trainingsplatform</h1>
          <p>Log in met je Google-account om verder te gaan.</p>
          <button className="btn-google" onClick={handleLogin}>
            <GoogleIcon />
            Inloggen met Google
          </button>
          {loginError && <p className="login-error">{loginError}</p>}
        </div>
      </div>
    );
  }

  return (
    <div className="app">
      <header className="app-header">
        <div className="app-header-inner">
          <div className="app-logo">
            <a
              href="https://adapta.nl/workspace/"
              target="_blank"
              rel="noopener noreferrer"
            >
              <img
                src="https://adapta.nl/wp-content/uploads/2023/11/adaptalogos_workspace.svg"
                alt="Adapta Workspace"
              />
            </a>
          </div>
          <div className="app-header-right">
            <div className="avatar-wrap" ref={avatarRef}>
              <button
                className="user-avatar"
                onClick={() => setAvatarOpen((o) => !o)}
                aria-label="Profiel"
              >
                {(user.displayName || user.email).charAt(0).toUpperCase()}
              </button>
              {avatarOpen && (
                <div className="avatar-dropdown">
                  <div className="avatar-dropdown-name">{user.displayName || user.email}</div>
                  <hr className="avatar-dropdown-divider" />
                  <button className="avatar-dropdown-logout" onClick={handleLogout}>
                    Uitloggen
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      <main className="app-main">
        {role === "trainer" || role === "admin" ? (
          <TrainerView
            sessions={sessions}
            role={role}
            onAdd={handleAddSession}
            onUpdate={handleUpdateSession}
            onDelete={handleDeleteSession}
          />
        ) : (
          <EmployeeView
            sessions={sessions}
            user={user}
            onRegister={handleRegister}
            onUnregister={handleUnregister}
          />
        )}
      </main>
    </div>
  );
}

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18">
      <path
        fill="#4285F4"
        d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"
      />
      <path
        fill="#34A853"
        d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z"
      />
      <path
        fill="#FBBC05"
        d="M3.964 10.706A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.706V4.962H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.038l3.007-2.332z"
      />
      <path
        fill="#EA4335"
        d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.962L3.964 7.294C4.672 5.163 6.656 3.58 9 3.58z"
      />
    </svg>
  );
}
